import {
    ApplicationDependencyService, ApplicationGroupingService, ApplicationServerService, DatabaseApplicationService, SchemaService,
} from '@amzn/awscat-portfolio-assessment-service-client';
import {
    AppDependency, ApplicationGroup, ApplicationToServer, Database, DatabaseApplication, DeserializedApplication, DeserializedServer
} from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Header, SpaceBetween, Tabs, TabsProps } from '@amzn/awsui-components-react';
import { useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router-dom';
import { useIsMounted } from 'react-tidy';

import { constants } from '../../../common/Localization';
import AWSCATTable from '../../../common/table/Table-index';
import { ColumnDefinition, ColumnTypes } from '../../../common/table/types';
import { MatchParam } from '../../../common/types';
import { ColumnOrderType, EXCLUDED_APPLICATION_COLUMNS, EXCLUDED_DATABASE_COLUMNS, EXCLUDED_SERVER_COLUMNS } from '../../portfolio-data-asset/constants';
import { SchemaPropertyMap } from '../../portfolio-data-asset/types';
import { generateColumnDefs } from '../../portfolio-data-asset/utils';
import ApplicationGroupDependencyGraph from './ApplicationGroupDependencyGraph';
import { dependencyTypeMapping, getDisplayResourceId } from './ApplicationGroupHelper';

type Props = FlashContextInterface;

const applicationGroupingService = new ApplicationGroupingService();
const schemaService = new SchemaService();
const appDependencyService = new ApplicationDependencyService();
const applicationServerService = new ApplicationServerService();
const databaseApplicationService = new DatabaseApplicationService();

export type ServerCommunicationDetail = {
    src: string;
    dst: string;
    flow: string;
};

function ApplicationGroupDetail({ flash }: Props) {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/planning/grouping/:groupId',
    });

    const portfolioId = match?.params?.portfolioId;
    const groupId = match?.params?.groupId;

    const isMounted = useIsMounted();


    const [ activeTabId, setActiveTabId ] = useState('applications');
    const [ loading, setLoading ] = useState(false);
    const [ groupDetail, setGroupDetail ] = useState<ApplicationGroup | null>(null);
    const [ applicationColumnDefinitions, setApplicationColumnDefinitions ] = useState<ColumnDefinition<DeserializedApplication>[]>([]);
    const [ serverColumnDefinitions, setServerColumnDefinitions ] = useState<ColumnDefinition<DeserializedServer>[]>([]);
    const [ databaseColumnDefinitions, setDatabaseColumnDefinitions ] = useState<ColumnDefinition<Database>[]>([]);
    const [ dependencies, setDependencies ] = useState<AppDependency[] | null>(null);
    const [ appServerMappings, setAppServerMappings ] = useState<ApplicationToServer[]>([]);
    const [ appDatabaseMappings, setAppDatabaseMappings ] = useState<DatabaseApplication[]>([]);

    const loadApplicationGroupData = async (): Promise<void> => {
        try {
            if (groupId && portfolioId) {
                const retrievedGroupDetail = await applicationGroupingService.get(portfolioId, groupId);

                // Load application schemas
                const appSchemas = await schemaService.listAttributes('applications', portfolioId);
                const serverSchemas = await schemaService.listAttributes('servers', portfolioId);
                const databaseSchemas = await schemaService.listAttributes('databases', portfolioId);

                const applicationHeaders: SchemaPropertyMap = {};
                appSchemas.forEach(appSchema => {
                    applicationHeaders[appSchema.value] = appSchema;
                });

                const serverHeaders: SchemaPropertyMap = {};
                serverSchemas.forEach(serverSchema => {
                    serverHeaders[serverSchema.value] = serverSchema;
                });

                const databaseHeaders: SchemaPropertyMap = {};
                databaseSchemas.forEach(dbSchema => {
                    databaseHeaders[dbSchema.value] = dbSchema;
                });

                // Build column definitions
                const appColumnDefinitions = generateColumnDefs(retrievedGroupDetail.applications, applicationHeaders, 
                    ColumnOrderType.APPLICATION, EXCLUDED_APPLICATION_COLUMNS);
                const serverColumnDefinitions = generateColumnDefs(retrievedGroupDetail.servers, serverHeaders, 
                    ColumnOrderType.SERVER, EXCLUDED_SERVER_COLUMNS);
                const databaseColumnDefinitions = generateColumnDefs(retrievedGroupDetail.databases, databaseHeaders, 
                    ColumnOrderType.DATABASE, EXCLUDED_DATABASE_COLUMNS);
                
                if (isMounted()) {
                    setGroupDetail(retrievedGroupDetail);
                    setApplicationColumnDefinitions(appColumnDefinitions);
                    setServerColumnDefinitions(serverColumnDefinitions);
                    setDatabaseColumnDefinitions(databaseColumnDefinitions);
                }
            }
        } catch(err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_APPLICATION_GROUP_DETAIL);
            }
        }
    };

    const loadApplicationDependenciesMappings = async (): Promise<void> => {
        try {
            if (portfolioId) {
                const appDependencies = (await appDependencyService.list(portfolioId)).filter(dependeny => dependeny.inScope);
                if (isMounted()) {
                    setDependencies(appDependencies);
                }
            }
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_APPLICATION_DEPENDENCIES_MAPPING);
            }
        }
    };

    const loadApplicationServerMappings = async (): Promise<void> => {
        try {
            if (portfolioId) {
                const appServer = await applicationServerService.list(portfolioId);
                setAppServerMappings(appServer);
            }
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_APPLICATION_SERVER_MAPPING);
        }
    };

    const loadApplicationDatabaseMappings = async (): Promise<void> => {
        try {
            if (portfolioId) {
                const appDatabase = await databaseApplicationService.list(portfolioId);
                setAppDatabaseMappings(appDatabase);
            }
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_APPLICATION_DATABASE_MAPPING);
        }
    };

    const loadData = async (): Promise<void> => {
        setLoading(true);
        await loadApplicationGroupData();
        await loadApplicationDependenciesMappings();
        await loadApplicationServerMappings();
        await loadApplicationDatabaseMappings();
        if (isMounted()) {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ portfolioId, groupId ]);

    const onGroupDetailUpdate = (newGroupDetail: ApplicationGroup): void => setGroupDetail(newGroupDetail);

    const buildAppTable = (applications: DeserializedApplication[], loading: boolean): JSX.Element => {
        return (
            <AWSCATTable
                resourceName={constants.APPLICATIONS}
                items={applications}
                columnDefinitions={applicationColumnDefinitions}
                loading={loading}
            />
        );
    };

    const buildServerTable = (servers: DeserializedServer[], loading: boolean): JSX.Element => {
        return (
            <AWSCATTable
                resourceName={constants.SERVERS}
                items={servers}
                columnDefinitions={serverColumnDefinitions}
                loading={loading}
            />
        );
    };

    const buildDatabaseTable = (databases: Database[], loading: boolean): JSX.Element => {
        return (
            <AWSCATTable
                resourceName={constants.DATABASES}
                items={databases}
                columnDefinitions={databaseColumnDefinitions}
                loading={loading}
            />
        );
    };

    const dependencyColumnDefinition: ColumnDefinition<AppDependency>[] = [
        {
            cell: item => item.srcAppId,
            id: 'srcAppId',
            header: constants.APPLICATION,
            type: ColumnTypes.TEXT,
        },
        {
            data: item => getDisplayResourceId(item.resourceType, item.resourceId),
            cell: item => getDisplayResourceId(item.resourceType, item.resourceId),
            id: 'resourceId',
            header: constants.RESOURCE_ID,
            type: ColumnTypes.TEXT,
        },
        {
            cell: item => item.destAppId,
            id: 'destAppId',
            header: constants.DEPENDENT_APPLICATION,
            type: ColumnTypes.TEXT,
        },
        {
            data: item => dependencyTypeMapping[item.resourceType],
            cell: item => dependencyTypeMapping[item.resourceType],
            id: 'resourceType',
            header: constants.DEPENDENCY_TYPE,
            type: ColumnTypes.TEXT,
        },
        {
            data: item => item.status === 'active' ? constants.ACTIVE : constants.INACTIVE,
            cell: item => item.status === 'active' ? constants.ACTIVE : constants.INACTIVE,
            id: 'status',
            header: constants.STATUS,
            type: ColumnTypes.TEXT,
        },
        {
            data: item => item.isSharedInfrastructure ? constants.TRUE : constants.FALSE,
            cell: item => item.isSharedInfrastructure ? constants.TRUE : constants.FALSE,
            id: 'isSharedInfrastructure',
            header: constants.IS_SHARED_INFRASTRUCTURE,
            type: ColumnTypes.TEXT,
        },
    ];

    const buildDependencyTable = (dependencies: AppDependency[], loading: boolean): JSX.Element => {
        return (
            <AWSCATTable
                resourceName={constants.DEPENDENCIES}
                items={dependencies}
                columnDefinitions={dependencyColumnDefinition}
                loading={loading}
                enableAutoSorting
                autoGenerateVisibleContent
                useDefaultDownload
            />
        );
    };

    const buildTabs = (group: ApplicationGroup | null): TabsProps.Tab[] => {
        const applicationTable = buildAppTable(group?.applications ?? [], loading);
        const serverTable = buildServerTable(group?.servers ?? [], loading);
        const databaseTable = buildDatabaseTable(group?.databases ?? [], loading);
        const dependencyTable = buildDependencyTable(dependencies ?? [], loading);

        return [
            {
                id: 'applications',
                label: constants.APPLICATIONS,
                content: applicationTable,
            },
            {
                id: 'servers',
                label: constants.SERVERS,
                content: serverTable,
            },
            {
                id: 'databases',
                label: constants.DATABASES,
                content: databaseTable,
            },
            {
                id: 'dependencies',
                label: constants.DEPENDENCIES,
                content: dependencyTable,
            },
        ];
    };

    const groupName = groupDetail ? `- ${groupDetail.name}` : '';

    return (
        <SpaceBetween size='l'>
            <Header variant='h1'>{constants.APPLICATION_GROUP_DETAIL} {groupName}</Header>
            <ApplicationGroupDependencyGraph
                appDependencies={dependencies}
                groupDetail={groupDetail}
                loadingGroupDetail={loading}
                onGroupDetailUpdate={onGroupDetailUpdate}
                appServerMappings={appServerMappings}
                appDatabaseMappings={appDatabaseMappings}
            />
            <Tabs tabs={buildTabs(groupDetail)} activeTabId={activeTabId} onChange={({ detail }) => setActiveTabId(detail.activeTabId)} />
        </SpaceBetween>
    );
}

export default withFlashContext(ApplicationGroupDetail);
